import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ArrowRight, Menu, Moon, Sun } from 'lucide-react';
import { useTheme } from 'next-themes';
import { Button } from '@/components/ui/button';
import { Logo } from '@/components/ui/Logo';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';

type LandingLink = { label: string; href: string; section?: string };

const links: LandingLink[] = [
  { label: 'Before you go', href: '/#before-you-go', section: 'before-you-go' },
  { label: 'Who it\u2019s for', href: '/#who-its-for', section: 'who-its-for' },
  { label: 'Pricing', href: '/pricing' },
  { label: 'Journal', href: '/blog' },
  { label: 'Help', href: '/help' },
];

const scrollToSection = (id: string) => {
  const target = document.getElementById(id);
  if (!target) return false;
  target.scrollIntoView({ behavior: 'smooth', block: 'start' });
  return true;
};

export const LandingHeader = () => {
  const [open, setOpen] = useState(false);
  const { resolvedTheme, setTheme } = useTheme();
  const location = useLocation();

  const handleLinkClick = (event: React.MouseEvent, link: LandingLink) => {
    if (!link.section || location.pathname !== '/') return;
    event.preventDefault();
    const fromSheet = open;
    setOpen(false);
    // The mobile sheet locks page scroll while open; scrolling before it finishes
    // closing gets snapped back to the top, so wait for the close to settle.
    if (fromSheet) {
      const section = link.section;
      window.setTimeout(() => scrollToSection(section), 380);
    }
    else scrollToSection(link.section);
  };

  return (
    <header className="border-b border-border">
      <div className="home-container flex h-20 items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-3">
          <Link to="/" aria-label="OdyVoy home" className="flex h-12 w-12 shrink-0 items-center justify-center rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2">
            <Logo src="/lovable-uploads/odyvoy-icon.png" className="h-9 w-9" alt="OdyVoy logo" />
          </Link>
          <nav aria-label="Main navigation" className="hidden items-center gap-7 md:flex">
            {links.map(link => (
              <Button key={link.label} variant="ghost" asChild className="h-12 rounded-none px-0 text-foreground hover:bg-transparent hover:text-primary">
                <Link to={link.href} className="no-underline" onClick={event => handleLinkClick(event, link)}>{link.label}</Link>
              </Button>
            ))}
          </nav>
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="h-12 w-12 md:hidden" aria-label="Open navigation menu"><Menu /></Button>
            </SheetTrigger>
            <SheetContent>
              <SheetHeader><SheetTitle>OdyVoy</SheetTitle></SheetHeader>
              <nav aria-label="Mobile navigation" className="mt-8 flex flex-col gap-3">
                {links.map(link => (
                  <Button key={link.label} asChild variant="ghost" className="min-h-12 justify-start" onClick={() => setOpen(false)}>
                    <Link to={link.href} onClick={event => handleLinkClick(event, link)}>{link.label}</Link>
                  </Button>
                ))}
              </nav>
            </SheetContent>
          </Sheet>
        </div>
        <div className="flex items-center gap-4">
          <Button size="icon" variant="ghost" className="h-12 w-12 text-primary" aria-label={resolvedTheme === 'dark' ? 'Switch to light theme' : 'Switch to dark theme'} onClick={() => setTheme(resolvedTheme === 'dark' ? 'light' : 'dark')}>
            {resolvedTheme === 'dark' ? <Sun /> : <Moon />}
          </Button>
          <Button asChild variant="ghost" className="h-12 px-0 text-primary hover:bg-transparent">
            <Link to="/auth" className="no-underline">Log In <ArrowRight aria-hidden="true" /></Link>
          </Button>
        </div>
      </div>
    </header>
  );
};
